/**
 * Timezone helpers (N2b) — IANA/DST-safe via Intl.DateTimeFormat. NO manual
 * offset math. Used for the follow-up "today" boundary + the world clock.
 *
 * Follow-ups are INTERNAL: a rep's own reminder in their own wall-clock time.
 * "today"/"tomorrow" for the badge + dashboard are computed in the logged-in
 * user's timezone (fixes the UTC off-by-one). Outward-facing client-timezone
 * scheduling ("Consultation") is a FUTURE feature — do not add it here.
 */

export const DEFAULT_TZ = 'America/Toronto';

/** The logged-in user's IANA timezone, defaulting to Eastern. */
export function userTz(user: { timezone?: string | null } | null | undefined): string {
  return user?.timezone || DEFAULT_TZ;
}

/** 'YYYY-MM-DD' for `now` in the given IANA zone (DST-safe). */
export function todayInTz(tz: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** Add n days to a 'YYYY-MM-DD' string — pure calendar math, tz-agnostic. */
export function addDaysStr(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Day-of-week for a 'YYYY-MM-DD' (0=Sun … 6=Sat). A calendar date's weekday is
 * fixed regardless of timezone, so no tz argument is needed. */
export function weekdayIndex(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** The UTC instant (ISO) of local midnight for a 'YYYY-MM-DD' in an IANA zone.
 * Lets a caller turn a tz-aware calendar boundary into an absolute instant so a
 * server can compare `created_at >= :instant` with no timezone knowledge of its
 * own. DST-safe: probes the zone's offset at that date via Intl. */
export function startOfDayInTz(dateStr: string, tz: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const utcMidnight = Date.UTC(y, m - 1, d);
  try {
    // en-US stays here, and it is not a stray: this format is never DISPLAYED.
    // It exists to read the zone's offset back out of formatToParts as numbers,
    // so the locale only has to produce 2-digit numeric parts — which every
    // locale does. Changing it would be churn with a small chance of breaking
    // the part names the lines below index into.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(utcMidnight));
    const g: Record<string, string> = {};
    parts.forEach((p) => { if (p.type !== 'literal') g[p.type] = p.value; });
    const asIfUtc = Date.UTC(+g.year, +g.month - 1, +g.day, +g.hour, +g.minute, +g.second);
    const offset = asIfUtc - utcMidnight; // how far the zone leads UTC at that date
    return new Date(utcMidnight - offset).toISOString();
  } catch {
    return new Date(utcMidnight).toISOString();
  }
}

/** Current wall-clock (24h HH:MM) + weekday for a zone — for the world clock. */
export function clockInTz(tz: string, now: Date = new Date()): { time: string; weekday: string } {
  try {
    const time = new Intl.DateTimeFormat('en-CA', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(now);
    const weekday = new Intl.DateTimeFormat('en-CA', { timeZone: tz, weekday: 'short' }).format(now);
    return { time, weekday };
  } catch {
    return { time: '--:--', weekday: '' };
  }
}

/** Format an absolute timestamp (ISO) in a given IANA zone — DST-safe. Used for
 * real moments like completed_at, which must read in the viewer's timezone. */
export function formatDateTimeInTz(iso: string | null | undefined, tz: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(iso));
  } catch {
    return String(iso);
  }
}

/** A readable city/label from an IANA zone ('Europe/Warsaw' -> 'Warsaw'). */
export function tzCityLabel(tz: string): string {
  const last = tz.split('/').pop() ?? tz;
  return last.replace(/_/g, ' ');
}

/** Trim a stored TIME ('14:30:00' -> '14:30'); returns '' for empty. */
export function shortTime(t: string | null | undefined): string {
  if (!t) return '';
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(t);
}

// Timezone options for the per-user selector (Canada coast-to-coast + common).
export const TZ_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/Vancouver', label: 'Vancouver (Pacific)' },
  { value: 'America/Edmonton', label: 'Calgary / Edmonton (Mountain)' },
  { value: 'America/Winnipeg', label: 'Winnipeg (Central)' },
  { value: 'America/Toronto', label: 'Toronto (Eastern)' },
  { value: 'America/Halifax', label: 'Halifax (Atlantic)' },
  { value: 'America/St_Johns', label: "St. John's (Newfoundland)" },
  { value: 'Europe/Warsaw', label: 'Warsaw (Central Europe)' },
  { value: 'Europe/London', label: 'London (UK)' },
  { value: 'America/New_York', label: 'New York (US Eastern)' },
  { value: 'America/Chicago', label: 'Chicago (US Central)' },
  { value: 'America/Denver', label: 'Denver (US Mountain)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (US Pacific)' },
  { value: 'UTC', label: 'UTC' },
];
